import { useState } from 'react';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setSubmitStatus('success');
      setEmail('');
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d8uip7j9akq3s3aso9r0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <footer className="bg-foreground-900 rounded-t-2xl mx-4 md:mx-6 mt-8">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          <div>
            <Link
              to="/"
              className="text-xl font-heading font-bold text-background-50 tracking-tight"
            >
              allchinabuy<span className="text-primary-400">.info</span>
            </Link>
            <p className="text-sm text-foreground-400 mt-4 leading-relaxed max-w-xs">
              Your trusted source for the best AllChinaBuy Spreadsheet. Updated weekly with verified sellers and quality-checked finds.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">Navigation</h4>
            <div className="flex flex-col gap-2.5">
              <Link to="/" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">Home</Link>
              <Link to="/guide" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">Buying Guide</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">AllChinaBuy Sheet</a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">Buy It Now</a>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">Categories</h4>
            <div className="flex flex-col gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">Stay Updated</h4>
            <p className="text-sm text-foreground-400 mb-4">
              Get notified about new finds and spreadsheet updates.
            </p>
            <form
              onSubmit={handleNewsletterSubmit}
              data-readdy-form
              className="flex gap-0"
            >
              <div className="spam-protection-field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                className="flex-1 px-4 py-2.5 bg-foreground-800 text-background-50 text-sm rounded-l-full border border-foreground-700 focus:outline-none focus:border-primary-500 placeholder:text-foreground-500"
              />
              <button
                type="submit"
                disabled={submitStatus === 'submitting'}
                className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-r-full text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {submitStatus === 'submitting' ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-send-plane-fill"></i>
                )}
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-xs text-accent-400 mt-2">Thank you! You are now subscribed.</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-xs text-red-400 mt-2">Something went wrong. Please try again.</p>
            )}
          </div>
        </div>

        <div className="border-t border-foreground-800 mt-10 pt-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-foreground-500">
              &copy; {new Date().getFullYear()} allchinabuy.info — An independent resource. Not affiliated with AllChinaBuy.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/guide" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors">Guide</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors">AllChinaBuy Sheet</a>
            </div>
          </div>
        </div>
      </div>

      <div className="relative overflow-hidden h-16 md:h-20">
        <p className="absolute bottom-0 left-1/2 -translate-x-1/2 text-4xl md:text-6xl font-heading font-bold text-foreground-800/15 whitespace-nowrap select-none pointer-events-none">
          ALLCHINABUY
        </p>
      </div>
    </footer>
  );
}